import { React, useState } from 'react';

import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DateRangeRoundedIcon from '@mui/icons-material/DateRangeRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import { downloadBlob } from '../../utils/csvExport';
import { formatDateLabelPtBr, formatIsoDate } from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import '../css/pvOperationAlerts.css';

const AlertasPVOperation = () => {
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [dateTimeInicio, setDateTimeInicio] = useState(null);
    const [errorInicio, setErrorInicio] = useState(false);
    const [dateTimeFinal, setDateTimeFinal] = useState(null);
    const [errorFinal, setErrorFinal] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    const handleAlerta = (texto, tipo) => {
        setMessage(texto);
        setStatus(tipo);
        setOpenAlerta(true);
    };

    async function submitAlertasPVOperation (e) {
        e.preventDefault();

        if (!dateTimeInicio) {
            setErrorInicio(true);
            return;
        }

        if (!dateTimeFinal) {
            setErrorFinal(true);
            return;
        }

        if (dateTimeFinal < dateTimeInicio) {
            handleAlerta('A data final deve ser posterior ou igual à data de início.', 'warning');
            return;
        }

        setErrorInicio(false);
        setErrorFinal(false);
        setIsSubmit(true);

        const inicio = formatIsoDate(dateTimeInicio);
        const fim = formatIsoDate(dateTimeFinal);
        const body = JSON.stringify({
            inicio,
            fim
        });

        try {
            const response = await fetchFlask('falhasPVOperation', body, true);

            if (response instanceof Blob && response.type.includes('application/json')) {
                const errorResponse = JSON.parse(await response.text());
                handleAlerta(errorResponse.message, errorResponse.status);
                return;
            }

            if (response instanceof Blob) {
                downloadBlob(`alertas_pv_operation_${inicio}_${fim}.csv`, response);

                handleAlerta('CSV de alertas gerado com sucesso.', 'success');
                return;
            }

            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
        } finally {
            setIsSubmit(false);
        }
    }

    const completedFields = [dateTimeInicio, dateTimeFinal].filter(Boolean).length;
    const inicioLabel = formatDateLabelPtBr(dateTimeInicio, 'Não selecionada');
    const fimLabel = formatDateLabelPtBr(dateTimeFinal, 'Não selecionada');
    const fileName = `alertas_pv_operation_${dateTimeInicio ? formatIsoDate(dateTimeInicio) : 'inicio'}_${dateTimeFinal ? formatIsoDate(dateTimeFinal) : 'fim'}.csv`;

    return (
        <Box className='mainBox pv-alerts-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>Alertas PV Operation</PageHeader>

            <form method='POST' onSubmit={submitAlertasPVOperation} className='pv-alerts-layout'>
                <Box className='pv-alerts-workspace'>
                    <Box className='pv-alerts-intro'>
                        <Box>
                            <span className='pv-alerts-kicker'>O&M</span>
                            <h2>Exportação de alertas</h2>
                        </Box>
                        <p>
                            Defina a janela de análise para baixar o CSV de falhas registradas pelo PV Operation.
                        </p>
                    </Box>

                    <Box className='pv-alerts-panel'>
                        <Box className='pv-alerts-section-heading'>
                            <DateRangeRoundedIcon />
                            <Box>
                                <span>Período</span>
                                <strong>Janela de alertas</strong>
                            </Box>
                        </Box>

                        <Box className='pv-alerts-fields-grid'>
                            <FormControl className='pv-alerts-date-control' error={errorInicio}>
                                <DayDatePicker
                                    id='dataInicio'
                                    label='Data de início'
                                    minDate={new Date('2023-01-02')}
                                    maxDate={new Date()}
                                    value={dateTimeInicio}
                                    onChange={(value) => {
                                        setDateTimeInicio(value);
                                        setErrorInicio(false);
                                    }}
                                />
                                {errorInicio && (
                                    <FormHelperText>Por favor selecione a data de início.</FormHelperText>
                                )}
                            </FormControl>

                            <FormControl className='pv-alerts-date-control' error={errorFinal}>
                                <DayDatePicker
                                    id='dataFinal'
                                    label='Data final'
                                    minDate={new Date('2023-01-02')}
                                    maxDate={new Date()}
                                    value={dateTimeFinal}
                                    onChange={(value) => {
                                        setDateTimeFinal(value);
                                        setErrorFinal(false);
                                    }}
                                />
                                {errorFinal && (
                                    <FormHelperText>Por favor selecione a data final.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='pv-alerts-export-strip'>
                        <FileDownloadRoundedIcon />
                        <Box>
                            <span>Arquivo gerado</span>
                            <strong>{fileName}</strong>
                        </Box>
                    </Box>

                    <Box className='pv-alerts-rail'>
                        <Box>
                            <CalendarMonthRoundedIcon />
                            <span>Início</span>
                            <strong>{inicioLabel}</strong>
                        </Box>
                        <Box>
                            <WarningAmberRoundedIcon />
                            <span>Fonte</span>
                            <strong>PV Operation</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='pv-alerts-summary'>
                    <Box className='pv-alerts-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da exportação</strong>
                        </Box>
                    </Box>

                    <Box className='pv-alerts-summary-list'>
                        <Box>
                            <span>Data de início</span>
                            <strong className={!dateTimeInicio ? 'is-empty' : undefined}>{inicioLabel}</strong>
                        </Box>
                        <Box>
                            <span>Data final</span>
                            <strong className={!dateTimeFinal ? 'is-empty' : undefined}>{fimLabel}</strong>
                        </Box>
                        <Box>
                            <span>Entrega</span>
                            <strong>Download automático</strong>
                        </Box>
                        <Box>
                            <span>Formato</span>
                            <strong>CSV</strong>
                        </Box>
                    </Box>

                    <Box className='pv-alerts-attention'>
                        <NotificationsActiveRoundedIcon />
                        <p>
                            O arquivo consolida os alertas do intervalo selecionado para conferência operacional.
                        </p>
                    </Box>

                    <Box className='pv-alerts-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/2 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<TableChartRoundedIcon />}>
                        Gerar CSV
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default AlertasPVOperation;
